import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";
import { useApi } from "../../hooks/useApi";
import { formatFileSize } from "../../utils/formatFileSize";
import { HasPermission } from "../../functions/auth";
import { PERMISSIONS } from "../../constants/permissions";

interface DisputeDocumentItem {
  dispute_document_id: number;
  file_name: string;
  content_type: string;
  size_bytes: number;
  created_at: string | null;
  uploaded_by_first_name: string | null;
  uploaded_by_last_name: string | null;
}

export default function DisputeDocuments({ disputeId, open, version, onNotify }: {
  disputeId: number;
  open: boolean;
  version: number;
  onNotify: (message: string, severity: "success" | "error") => void;
}) {
  // Separate instances so `loading` tracks the list fetch only, not the delete.
  const { get, loading } = useApi();
  const { del, download } = useApi();
  const [documents, setDocuments] = useState<DisputeDocumentItem[] | null>(null);
  const [error, setError] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState<DisputeDocumentItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setError(false);
    try {
      const res = await get<{ documents: DisputeDocumentItem[] }>(`/api/disputes/${disputeId}/documents`);
      setDocuments(res.documents);
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    }
  }, [get, disputeId]);

  // Unlike the activity feed, documents change while the row is open (uploads
  // bump `version`), so refetch instead of caching the first load.
  useEffect(() => {
    if (open) fetchDocuments();
  }, [open, version, fetchDocuments]);

  const handleDownload = async (doc: DisputeDocumentItem) => {
    try {
      await download(`/api/disputes/${disputeId}/documents/${doc.dispute_document_id}/download`);
    } catch (err: any) {
      Sentry.captureException(err);
      onNotify(err?.message || "Failed to download document", "error");
    }
  };

  const handleDelete = async () => {
    if (!confirmDoc || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await del(`/api/disputes/${disputeId}/documents/${confirmDoc.dispute_document_id}`);
      setConfirmDoc(null);
      onNotify("Document deleted", "success");
      fetchDocuments();
    } catch (err: any) {
      Sentry.captureException(err);
      setDeleteError(err?.message || "Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !documents) {
    return <CircularProgress size={18} />;
  }
  if (error) {
    return <Typography variant="body2" color="error">Couldn&apos;t load documents — reopen the row to retry.</Typography>;
  }
  if (documents && documents.length === 0) {
    return <Typography variant="body2" color="text.secondary">No documents uploaded yet.</Typography>;
  }
  if (!documents) return null;

  return (
    <>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {documents.map((doc) => (
          <Box component="li" key={doc.dispute_document_id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Link component="button" type="button" variant="body2" onClick={() => handleDownload(doc)} sx={{ fontWeight: 500 }}>
                {doc.file_name}
              </Link>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {formatFileSize(doc.size_bytes)} · {[doc.uploaded_by_first_name, doc.uploaded_by_last_name].filter(Boolean).join(" ") || "Unknown"}
                {doc.created_at ? ` · ${dayjs(doc.created_at).format("MMM D, YYYY HH:mm")}` : ""}
              </Typography>
            </Box>
            <HasPermission permission={PERMISSIONS.DISPUTE_DELETE_FILE}>
              <IconButton size="small" aria-label="delete document" onClick={() => { setDeleteError(null); setConfirmDoc(doc); }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </HasPermission>
          </Box>
        ))}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDoc} onClose={() => !deleting && setConfirmDoc(null)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
          )}
          <DialogContentText>
            Delete &quot;{confirmDoc?.file_name}&quot;? Agents will no longer see or download it.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDoc(null)} color="inherit" disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
